import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Database, UserCheck, AlertCircle, Mail } from 'lucide-react';

const Privacy = () => {
  const { i18n } = useTranslation();

  const content = {
    fr: {
      title: "Politique de Confidentialité",
      subtitle: "Protection de vos données personnelles",
      lastUpdate: "Dernière mise à jour : Décembre 2025",
      intro: "EDENLIFE International s'engage à protéger votre vie privée. Cette politique explique comment nous collectons, utilisons et protégeons vos informations personnelles lorsque vous utilisez la plateforme Marathon de l'Avenir EDENLIFE 2026.",
      sections: [
        {
          icon: Database,
          title: "1. Données Collectées",
          content: "Nous collectons les informations suivantes lors de votre inscription et utilisation de la plateforme :",
          list: [
            "Informations d'identification : nom complet, adresse email",
            "Informations de connexion : mot de passe (hashé et crypté) ou données Google OAuth",
            "Informations de paiement : traitées par nos partenaires sécurisés (Stripe), nous ne stockons jamais vos données de carte",
            "Historique des transactions : achats de billets, montants, dates",
            "Données de navigation : adresse IP, type de navigateur, pages visitées (cookies essentiels)",
            "Informations d'affiliation : code de parrainage, statistiques de ventes"
          ]
        },
        {
          icon: Eye,
          title: "2. Utilisation des Données",
          content: "Vos données personnelles sont utilisées exclusivement pour :",
          list: [
            "Créer et gérer votre compte utilisateur",
            "Traiter vos achats de billets et générer vos numéros",
            "Vous envoyer des confirmations d'achat et notifications importantes",
            "Vous contacter en cas de gain lors du tirage au sort",
            "Calculer et verser les commissions d'affiliation",
            "Améliorer nos services et l'expérience utilisateur",
            "Respecter nos obligations légales et fiscales"
          ]
        },
        {
          icon: Lock,
          title: "3. Protection des Données",
          content: "La sécurité de vos données est notre priorité absolue :",
          list: [
            "Cryptage SSL/TLS pour toutes les communications",
            "Mots de passe hashés avec l'algorithme bcrypt",
            "Paiements traités par Stripe, certifié PCI-DSS",
            "Serveurs sécurisés avec accès restreint",
            "Audits de sécurité réguliers",
            "Aucune vente de données à des tiers"
          ]
        },
        {
          icon: UserCheck,
          title: "4. Vos Droits",
          content: "Conformément aux lois sur la protection des données, vous avez le droit de :",
          list: [
            "Accéder à vos données personnelles",
            "Corriger ou mettre à jour vos informations",
            "Demander la suppression de votre compte (sous réserve des obligations légales)",
            "Retirer votre consentement à tout moment",
            "Exporter vos données dans un format portable",
            "Vous opposer au traitement de vos données"
          ]
        },
        {
          icon: null,
          title: "5. Cookies",
          content: "Nous utilisons des cookies essentiels pour le bon fonctionnement de la plateforme :",
          list: [
            "Cookies d'authentification : maintenir votre session active",
            "Cookies de préférences : langue et devise sélectionnées",
            "Cookies de sécurité : protection contre les attaques"
          ],
          note: "Nous n'utilisons PAS de cookies de tracking publicitaire. Les cookies essentiels ne peuvent pas être désactivés car ils sont nécessaires au fonctionnement du site."
        },
        {
          icon: null,
          title: "6. Partage des Données",
          content: "Nous ne vendons jamais vos données. Nous partageons vos informations uniquement avec :",
          list: [
            "Stripe : pour le traitement sécurisé des paiements",
            "Services d'email : pour l'envoi de confirmations et notifications",
            "Autorités légales : uniquement si requis par la loi"
          ]
        },
        {
          icon: null,
          title: "7. Conservation des Données",
          content: "Nous conservons vos données :",
          list: [
            "Données de compte : tant que votre compte est actif",
            "Historique des transactions : 7 ans (obligations fiscales)",
            "Données de tirage au sort : jusqu'à la fin de la campagne + 2 ans",
            "En cas de suppression de compte : données anonymisées ou supprimées sous 30 jours"
          ]
        },
        {
          icon: null,
          title: "8. Transferts Internationaux",
          content: "Vos données peuvent être traitées au Canada et aux États-Unis (serveurs Stripe). Nous nous assurons que tous nos partenaires respectent des normes de protection équivalentes à celles du Canada et de l'Union Européenne."
        },
        {
          icon: null,
          title: "9. Mineurs",
          content: "La plateforme Marathon de l'Avenir EDENLIFE 2026 est réservée aux personnes de 18 ans et plus. Nous ne collectons pas sciemment de données concernant des mineurs."
        },
        {
          icon: null,
          title: "10. Modifications",
          content: "Nous pouvons modifier cette politique à tout moment. Les changements seront publiés sur cette page avec une nouvelle date de mise à jour. Pour les changements importants, nous vous informerons par email."
        }
      ],
      contactTitle: "Contact pour la Protection des Données",
      contactDesc: "Pour toute question concernant cette politique ou pour exercer vos droits, contactez-nous :",
      contactInfo: [
        "EDENLIFE International",
        "6205, Blvd. des Grandes Prairies, Suite 215-216",
        "Saint-Léonard, QC H1P 1A2, Canada",
        "📧 support@marathon-edenlifeintl.org",
        "📞 1 866-722-7007"
      ]
    },
    en: {
      title: "Privacy Policy",
      subtitle: "Protection of your personal data",
      lastUpdate: "Last updated: December 2025",
      intro: "EDENLIFE International is committed to protecting your privacy. This policy explains how we collect, use and protect your personal information when you use the EDENLIFE Marathon 2026 platform.",
      sections: [
        {
          icon: Database,
          title: "1. Data Collected",
          content: "We collect the following information during your registration and use of the platform:",
          list: [
            "Identification information: full name, email address",
            "Login information: password (hashed and encrypted) or Google OAuth data",
            "Payment information: processed by our secure partners (Stripe), we never store your card data",
            "Transaction history: ticket purchases, amounts, dates",
            "Browsing data: IP address, browser type, pages visited (essential cookies)",
            "Affiliate information: referral code, sales statistics"
          ]
        },
        {
          icon: Eye,
          title: "2. Data Usage",
          content: "Your personal data is used exclusively for:",
          list: [
            "Creating and managing your user account",
            "Processing your ticket purchases and generating your numbers",
            "Sending you purchase confirmations and important notifications",
            "Contacting you in case of winning in the draw",
            "Calculating and paying affiliate commissions",
            "Improving our services and user experience",
            "Complying with our legal and tax obligations"
          ]
        },
        {
          icon: Lock,
          title: "3. Data Protection",
          content: "The security of your data is our absolute priority:",
          list: [
            "SSL/TLS encryption for all communications",
            "Passwords hashed with bcrypt algorithm",
            "Payments processed by Stripe, PCI-DSS certified",
            "Secure servers with restricted access",
            "Regular security audits",
            "No sale of data to third parties"
          ]
        },
        {
          icon: UserCheck,
          title: "4. Your Rights",
          content: "In accordance with data protection laws, you have the right to:",
          list: [
            "Access your personal data",
            "Correct or update your information",
            "Request deletion of your account (subject to legal obligations)",
            "Withdraw your consent at any time",
            "Export your data in a portable format",
            "Object to the processing of your data"
          ]
        },
        {
          icon: null,
          title: "5. Cookies",
          content: "We use essential cookies for the proper functioning of the platform:",
          list: [
            "Authentication cookies: keep your session active",
            "Preference cookies: selected language and currency",
            "Security cookies: protection against attacks"
          ],
          note: "We do NOT use advertising tracking cookies. Essential cookies cannot be disabled as they are necessary for the site to function."
        },
        {
          icon: null,
          title: "6. Data Sharing",
          content: "We never sell your data. We share your information only with:",
          list: [
            "Stripe: for secure payment processing",
            "Email services: for sending confirmations and notifications",
            "Legal authorities: only if required by law"
          ]
        },
        {
          icon: null,
          title: "7. Data Retention",
          content: "We retain your data:",
          list: [
            "Account data: as long as your account is active",
            "Transaction history: 7 years (tax obligations)",
            "Draw data: until end of campaign + 2 years",
            "Upon account deletion: data anonymized or deleted within 30 days"
          ]
        },
        {
          icon: null,
          title: "8. International Transfers",
          content: "Your data may be processed in Canada and the United States (Stripe servers). We ensure that all our partners comply with protection standards equivalent to those of Canada and the European Union."
        },
        {
          icon: null,
          title: "9. Minors",
          content: "The EDENLIFE Marathon 2026 platform is reserved for people aged 18 and over. We do not knowingly collect data concerning minors."
        },
        {
          icon: null,
          title: "10. Modifications",
          content: "We may modify this policy at any time. Changes will be posted on this page with a new update date. For significant changes, we will inform you by email."
        }
      ],
      contactTitle: "Data Protection Contact",
      contactDesc: "For any questions regarding this policy or to exercise your rights, contact us:",
      contactInfo: [
        "EDENLIFE International",
        "6205 Blvd. des Grandes Prairies, Suite 215-216",
        "Saint-Leonard, QC H1P 1A2, Canada",
        "📧 support@marathon-edenlifeintl.org",
        "📞 1 866-722-7007"
      ]
    },
    ht: {
      title: "Politik Konfidansyalite",
      subtitle: "Pwoteksyon done pèsonèl ou yo",
      lastUpdate: "Dènye mizajou: Desanm 2025",
      intro: "EDENLIFE International angaje li pou pwoteje vi prive ou. Politik sa a eksplike kijan nou kolekte, itilize ak pwoteje enfòmasyon pèsonèl ou lè w itilize platfòm Maraton EDENLIFE 2026 la.",
      sections: [
        {
          icon: Database,
          title: "1. Done Kolekte",
          content: "Nou kolekte enfòmasyon sa yo pandan enskripsyon ou ak itilizasyon platfòm nan:",
          list: [
            "Enfòmasyon idantifikasyon: non konplè, adrès imel",
            "Enfòmasyon koneksyon: modpas (kripte) oswa done Google OAuth",
            "Enfòmasyon peman: trete pa patnè sekirize nou yo (Stripe), nou pa janm estoke done kat ou",
            "Istorik tranzaksyon: acha tikè, montan, dat",
            "Done navigasyon: adrès IP, tip navigatè, paj vizite (cookies esansyèl)",
            "Enfòmasyon afilyasyon: kòd parenaj, estatistik vant"
          ]
        },
        {
          icon: Eye,
          title: "2. Itilizasyon Done yo",
          content: "Done pèsonèl ou yo itilize eksklizivman pou:",
          list: [
            "Kreye ak jere kont itilizatè ou",
            "Trete acha tikè ou yo ak jenere nimewo ou yo",
            "Voye konfimasyon acha ak notifikasyon enpòtan",
            "Kontakte ou si ou genyen nan tiraj la",
            "Kalkile ak peye komisyon afilyasyon",
            "Amelyore sèvis nou yo ak eksperyans itilizatè",
            "Respekte obligasyon legal ak fiskal nou yo"
          ]
        },
        {
          icon: Lock,
          title: "3. Pwoteksyon Done yo",
          content: "Sekirite done ou yo se priyorite absolui nou:",
          list: [
            "Kriptaj SSL/TLS pou tout kominikasyon",
            "Modpas kripte ak algorithm bcrypt",
            "Peman trete pa Stripe, sètifye PCI-DSS",
            "Sèvè sekirize ak aksè restren",
            "Odit sekirite regilye",
            "Pa gen vant done bay twazyèm pati"
          ]
        },
        {
          icon: UserCheck,
          title: "4. Dwa Ou yo",
          content: "Selon lwa pwoteksyon done yo, ou gen dwa pou:",
          list: [
            "Gen aksè a done pèsonèl ou yo",
            "Korije oswa mete ajou enfòmasyon ou yo",
            "Mande pou efase kont ou (selon obligasyon legal)",
            "Retire konsantman ou nenpòt ki lè",
            "Ekspòte done ou yo nan yon fòma pòtab",
            "Opoze a tretman done ou yo"
          ]
        },
        {
          icon: null,
          title: "5. Cookies",
          content: "Nou itilize cookies esansyèl pou bon fonksyonman platfòm nan:",
          list: [
            "Cookies otantifikasyon: kenbe sesyon ou aktif",
            "Cookies preferans: lang ak lajan chwazi",
            "Cookies sekirite: pwoteksyon kont atak"
          ],
          note: "Nou PA itilize cookies tracking piblisite. Cookies esansyèl yo pa ka dezaktive paske yo nesesè pou sit la fonksyone."
        },
        {
          icon: null,
          title: "6. Pataj Done yo",
          content: "Nou pa janm vann done ou yo. Nou pataje enfòmasyon ou yo sèlman ak:",
          list: [
            "Stripe: pou tretman peman sekirize",
            "Sèvis imel: pou voye konfimasyon ak notifikasyon",
            "Otorite legal: sèlman si lalwa mande"
          ]
        },
        {
          icon: null,
          title: "7. Konsèvasyon Done yo",
          content: "Nou konsève done ou yo:",
          list: [
            "Done kont: toutotan kont ou aktif",
            "Istorik tranzaksyon: 7 an (obligasyon fiskal)",
            "Done tiraj: jiska fen kanpay + 2 an",
            "Lè efase kont: done anonimize oswa efase nan 30 jou"
          ]
        },
        {
          icon: null,
          title: "8. Transfè Entènasyonal",
          content: "Done ou yo ka trete nan Kanada ak Etazini (sèvè Stripe). Nou asire ke tout patnè nou yo respekte estanda pwoteksyon ekivalan a sa yo nan Kanada ak Inyon Ewopeyèn."
        },
        {
          icon: null,
          title: "9. Minè yo",
          content: "Platfòm Maraton EDENLIFE 2026 la rezève pou moun ki gen 18 an ak plis. Nou pa kolekte done konsènan minè yo konseyanman."
        },
        {
          icon: null,
          title: "10. Modifikasyon yo",
          content: "Nou ka modifye politik sa a nenpòt ki lè. Chanjman yo ap pibliye sou paj sa a ak yon nouvo dat mizajou. Pou chanjman enpòtan, nou ap enfòme ou pa imel."
        }
      ],
      contactTitle: "Kontak pou Pwoteksyon Done yo",
      contactDesc: "Pou nenpòt kesyon konsènan politik sa a oswa pou egzèse dwa ou yo, kontakte nou:",
      contactInfo: [
        "EDENLIFE International",
        "6205 Blvd. des Grandes Prairies, Suite 215-216",
        "Saint-Léonard, QC H1P 1A2, Kanada",
        "📧 support@marathon-edenlifeintl.org",
        "📞 1 866-722-7007"
      ]
    },
    es: {
      title: "Política de Privacidad",
      subtitle: "Protección de sus datos personales",
      lastUpdate: "Última actualización: Diciembre 2025",
      intro: "EDENLIFE International se compromete a proteger su privacidad. Esta política explica cómo recopilamos, utilizamos y protegemos su información personal cuando utiliza la plataforma Maratón EDENLIFE 2026.",
      sections: [
        {
          icon: Database,
          title: "1. Datos Recopilados",
          content: "Recopilamos la siguiente información durante su registro y uso de la plataforma:",
          list: [
            "Información de identificación: nombre completo, dirección de correo electrónico",
            "Información de conexión: contraseña (cifrada) o datos de Google OAuth",
            "Información de pago: procesada por nuestros socios seguros (Stripe), nunca almacenamos los datos de su tarjeta",
            "Historial de transacciones: compras de boletos, montos, fechas",
            "Datos de navegación: dirección IP, tipo de navegador, páginas visitadas (cookies esenciales)",
            "Información de afiliación: código de referido, estadísticas de ventas"
          ]
        },
        {
          icon: Eye,
          title: "2. Uso de Datos",
          content: "Sus datos personales se utilizan exclusivamente para:",
          list: [
            "Crear y gestionar su cuenta de usuario",
            "Procesar sus compras de boletos y generar sus números",
            "Enviarle confirmaciones de compra y notificaciones importantes",
            "Contactarle en caso de ganar en el sorteo",
            "Calcular y pagar las comisiones de afiliación",
            "Mejorar nuestros servicios y la experiencia del usuario",
            "Cumplir con nuestras obligaciones legales y fiscales"
          ]
        },
        {
          icon: Lock,
          title: "3. Protección de Datos",
          content: "La seguridad de sus datos es nuestra prioridad absoluta:",
          list: [
            "Cifrado SSL/TLS para todas las comunicaciones",
            "Contraseñas cifradas con algoritmo bcrypt",
            "Pagos procesados por Stripe, certificado PCI-DSS",
            "Servidores seguros con acceso restringido",
            "Auditorías de seguridad regulares",
            "Sin venta de datos a terceros"
          ]
        },
        {
          icon: UserCheck,
          title: "4. Sus Derechos",
          content: "De acuerdo con las leyes de protección de datos, usted tiene derecho a:",
          list: [
            "Acceder a sus datos personales",
            "Corregir o actualizar su información",
            "Solicitar la eliminación de su cuenta (sujeto a obligaciones legales)",
            "Retirar su consentimiento en cualquier momento",
            "Exportar sus datos en un formato portable",
            "Oponerse al procesamiento de sus datos"
          ]
        },
        {
          icon: null,
          title: "5. Cookies",
          content: "Utilizamos cookies esenciales para el correcto funcionamiento de la plataforma:",
          list: [
            "Cookies de autenticación: mantener su sesión activa",
            "Cookies de preferencias: idioma y moneda seleccionados",
            "Cookies de seguridad: protección contra ataques"
          ],
          note: "NO utilizamos cookies de seguimiento publicitario."
        },
        {
          icon: null,
          title: "6. Compartir Datos",
          content: "Nunca vendemos sus datos. Compartimos su información únicamente con:",
          list: [
            "Stripe: para el procesamiento seguro de pagos",
            "Servicios de correo: para enviar confirmaciones y notificaciones",
            "Autoridades legales: solo si lo requiere la ley"
          ]
        },
        {
          icon: null,
          title: "7. Conservación de Datos",
          content: "Conservamos sus datos:",
          list: [
            "Datos de cuenta: mientras su cuenta esté activa",
            "Historial de transacciones: 7 años (obligaciones fiscales)",
            "Datos del sorteo: hasta el final de la campaña + 2 años",
            "En caso de eliminación de cuenta: datos anonimizados o eliminados en 30 días"
          ]
        },
        { icon: null, title: "8. Transferencias Internacionales", content: "Sus datos pueden ser procesados en Canadá y Estados Unidos (servidores Stripe). Nos aseguramos de que todos nuestros socios cumplan con estándares de protección equivalentes." },
        { icon: null, title: "9. Menores", content: "La plataforma Maratón EDENLIFE 2026 está reservada para personas de 18 años o más. No recopilamos conscientemente datos de menores." },
        { icon: null, title: "10. Modificaciones", content: "Podemos modificar esta política en cualquier momento. Los cambios se publicarán en esta página con una nueva fecha de actualización." }
      ],
      contactTitle: "Contacto de Protección de Datos",
      contactDesc: "Para cualquier pregunta sobre esta política o para ejercer sus derechos, contáctenos:",
      contactInfo: [
        "EDENLIFE International",
        "6205 Blvd. des Grandes Prairies, Suite 215-216",
        "Saint-Léonard, QC H1P 1A2, Canadá",
        "📧 support@marathon-edenlifeintl.org",
        "📞 1 866-722-7007"
      ]
    },
    pt: {
      title: "Política de Privacidade",
      subtitle: "Proteção dos seus dados pessoais",
      lastUpdate: "Última atualização: Dezembro 2025",
      intro: "A EDENLIFE International compromete-se a proteger sua privacidade. Esta política explica como coletamos, utilizamos e protegemos suas informações pessoais quando você utiliza a plataforma Maratona EDENLIFE 2026.",
      sections: [
        {
          icon: Database,
          title: "1. Dados Coletados",
          content: "Coletamos as seguintes informações durante seu registro e uso da plataforma:",
          list: [
            "Informações de identificação: nome completo, endereço de email",
            "Informações de conexão: senha (criptografada) ou dados do Google OAuth",
            "Informações de pagamento: processadas por nossos parceiros seguros (Stripe), nunca armazenamos os dados do seu cartão",
            "Histórico de transações: compras de bilhetes, valores, datas",
            "Dados de navegação: endereço IP, tipo de navegador, páginas visitadas (cookies essenciais)",
            "Informações de afiliação: código de indicação, estatísticas de vendas"
          ]
        },
        {
          icon: Eye,
          title: "2. Uso de Dados",
          content: "Seus dados pessoais são utilizados exclusivamente para:",
          list: [
            "Criar e gerenciar sua conta de usuário",
            "Processar suas compras de bilhetes e gerar seus números",
            "Enviar confirmações de compra e notificações importantes",
            "Contatá-lo em caso de ganhar no sorteio",
            "Calcular e pagar as comissões de afiliação",
            "Melhorar nossos serviços e a experiência do usuário",
            "Cumprir nossas obrigações legais e fiscais"
          ]
        },
        {
          icon: Lock,
          title: "3. Proteção de Dados",
          content: "A segurança dos seus dados é nossa prioridade absoluta:",
          list: [
            "Criptografia SSL/TLS para todas as comunicações",
            "Senhas criptografadas com algoritmo bcrypt",
            "Pagamentos processados pelo Stripe, certificado PCI-DSS",
            "Servidores seguros com acesso restrito",
            "Auditorias de segurança regulares",
            "Sem venda de dados a terceiros"
          ]
        },
        {
          icon: UserCheck,
          title: "4. Seus Direitos",
          content: "De acordo com as leis de proteção de dados, você tem direito a:",
          list: [
            "Acessar seus dados pessoais",
            "Corrigir ou atualizar suas informações",
            "Solicitar a exclusão da sua conta (sujeito a obrigações legais)",
            "Retirar seu consentimento a qualquer momento",
            "Exportar seus dados em formato portável",
            "Opor-se ao processamento dos seus dados"
          ]
        },
        {
          icon: null,
          title: "5. Cookies",
          content: "Utilizamos cookies essenciais para o correto funcionamento da plataforma:",
          list: [
            "Cookies de autenticação: manter sua sessão ativa",
            "Cookies de preferências: idioma e moeda selecionados",
            "Cookies de segurança: proteção contra ataques"
          ],
          note: "NÃO utilizamos cookies de rastreamento publicitário."
        },
        {
          icon: null,
          title: "6. Compartilhamento de Dados",
          content: "Nunca vendemos seus dados. Compartilhamos suas informações apenas com:",
          list: [
            "Stripe: para processamento seguro de pagamentos",
            "Serviços de email: para enviar confirmações e notificações",
            "Autoridades legais: apenas se exigido por lei"
          ]
        },
        {
          icon: null,
          title: "7. Conservação de Dados",
          content: "Conservamos seus dados:",
          list: [
            "Dados da conta: enquanto sua conta estiver ativa",
            "Histórico de transações: 7 anos (obrigações fiscais)",
            "Dados do sorteio: até o final da campanha + 2 anos",
            "Em caso de exclusão de conta: dados anonimizados ou excluídos em 30 dias"
          ]
        },
        { icon: null, title: "8. Transferências Internacionais", content: "Seus dados podem ser processados no Canadá e Estados Unidos (servidores Stripe). Garantimos que todos os nossos parceiros cumpram padrões de proteção equivalentes." },
        { icon: null, title: "9. Menores", content: "A plataforma Maratona EDENLIFE 2026 é reservada para pessoas com 18 anos ou mais. Não coletamos conscientemente dados de menores." },
        { icon: null, title: "10. Modificações", content: "Podemos modificar esta política a qualquer momento. As alterações serão publicadas nesta página com uma nova data de atualização." }
      ],
      contactTitle: "Contato de Proteção de Dados",
      contactDesc: "Para qualquer pergunta sobre esta política ou para exercer seus direitos, entre em contato conosco:",
      contactInfo: [
        "EDENLIFE International",
        "6205 Blvd. des Grandes Prairies, Suite 215-216",
        "Saint-Léonard, QC H1P 1A2, Canadá",
        "📧 support@marathon-edenlifeintl.org",
        "📞 1 866-722-7007"
      ]
    }
  };

  const t = content[i18n.language] || content.fr;

  return (
    <div className="min-h-screen pt-24 px-6 pb-12" style={{ background: 'linear-gradient(135deg, #F4F1DE 0%, #E8E3D3 100%)' }}>
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-center mb-12">
            <Shield className="w-20 h-20 text-accent mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t.title}
            </h1>
            <p className="text-lg text-gray-600">{t.subtitle}</p>
            <p className="text-sm text-gray-500 mt-2">{t.lastUpdate}</p>
          </div>

          <div className="bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-8 md:p-12 space-y-8">
            <p className="text-gray-700 leading-relaxed text-lg">{t.intro}</p>
            
            {t.sections.map((section, index) => {
              const Icon = section.icon;
              return (
                <section key={index} className="border-b border-gray-200 pb-8 last:border-0 last:pb-0">
                  <div className="flex items-start space-x-3 mb-4">
                    {Icon && <Icon className="w-7 h-7 text-primary flex-shrink-0 mt-1" />}
                    <h2 className="text-xl font-bold text-primary">{section.title}</h2>
                  </div>
                  
                  <p className="text-gray-700 leading-relaxed mb-3 pl-0 md:pl-10">
                    {section.content}
                  </p>
                  
                  {section.list && (
                    <ul className="list-disc list-inside text-gray-700 space-y-2 pl-0 md:pl-10">
                      {section.list.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  )}
                  
                  {section.note && (
                    <p className="text-sm text-gray-600 mt-3 italic pl-0 md:pl-10">
                      {section.note}
                    </p>
                  )}
                </section>
              );
            })}

            <div className="mt-8 p-6 bg-accent/5 rounded-xl border-l-4 border-accent">
              <div className="flex items-start space-x-3">
                <Mail className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-primary mb-2">{t.contactTitle}</h3>
                  <p className="text-gray-700 mb-3">{t.contactDesc}</p>
                  <div className="text-gray-600 space-y-1">
                    {t.contactInfo.map((line, i) => (
                      <p key={i}>{line}</p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Privacy;
